import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Select,
  Chip,
} from '@mui/material';
import { useWeb3React } from '@web3-react/core';
import { Contract, ethers } from 'ethers';
import { useParams, useNavigate } from 'react-router-dom';

import AssetsKycManager from '../components/AssetsKycManager';
import { callContractWithRetry, createKYCContract, getKYCContract, getKYCContractWithSigner, getPolygonGasFees, Permission } from '../config/contracts';
import { toast } from 'react-toastify';
import PermissionGuard from '../components/PermissionGuard'; 
import { getPermissionColor, getPermissionText } from '../utils/walletUtils';
import { useSignMessageStore } from '../stores/signMessageStore';
import SignMessageDialog from '../components/SignMessageDialog';
const AssetsKycPage: React.FC = () => {
  return   <PermissionGuard requireConnection><AssetsKycContent /></PermissionGuard>;
};

const AssetsKycContent: React.FC = () => {
  const { tokenId: urlTokenId } = useParams<{ tokenId?: string }>();
  const navigate = useNavigate();
  const { account, provider,chainId } = useWeb3React();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenId, setTokenId] = useState(urlTokenId || '');
  const [walletAddress, setWalletAddress] = useState('');
  const [isWhitelisted, setIsWhitelisted] = useState(false); 
  const [permission, setPermission] = useState<Permission>(Permission.NONE);
  const [selectedKYCTypes, setSelectedKYCTypes] = useState<number[]>([]);
  // SignMessageDialog状态管理
  const {
    setOpen,
    setTitle,
    setSteps,
    setActiveStep,
    setMessage,
    setLoading: setDialogLoading,
    setError: setDialogError,
    nextStep,
    loading:dialogLoading,
    reset: resetDialog
  } = useSignMessageStore();
  const endsubmitjob = async ()=>{
      window.location.reload();
      // navigate(`/assets-kyc/${tokenId}`);
      setOpen(false);
      resetDialog();
  }

    // Get read-only contract instance
    const getContract = useCallback((): Contract | null => {
      if (!chainId || !provider) {
        console.warn('Failed to get contract instance: chainId or provider is not ready');
        return null;
      }
      return getKYCContract(chainId, provider);
    }, [chainId, provider]);
  
    // Get writable contract instance
    const getContractWithSigner = useCallback((): Contract | null => {
      if (!chainId || !provider || !account) {
        console.warn('Failed to get contract instance with signer: connection info is incomplete');
        return null;
      }
      try {
        const signer = provider.getSigner();
        return getKYCContractWithSigner(chainId, signer);
      } catch (err) {
        console.error('Failed to get contract instance with signer:', err);
        return null;
      }
    }, [chainId, provider, account]);

 
  const checkUserPermissions = async () => {
    try {
      setLoading(true);
      setError('');
      
      const contract = getContractWithSigner();
      if (!contract) {
        throw new Error('Please connect your wallet first');
      } 
      //  console.info('getContractWithSigner:', contract);   
        const whitelistInfo = await contract.getMyWhitelistInfo();
        //  console.info('whitelistInfo:', whitelistInfo);   
         // Check if exists in whitelist
        if (whitelistInfo.exists) {
          setPermission(whitelistInfo.permission);
          setSelectedKYCTypes(whitelistInfo.kycTypeIds.map((id: any) => id.toNumber()));
          setIsWhitelisted(whitelistInfo.exists);
        }else{
          setPermission(Permission.NONE);
          setSelectedKYCTypes([]);
          setIsWhitelisted(false);
        } 
      
    } catch (err: any) {
         console.error('Failed to check whitelist permissions:', err); 
          let errorMessage = 'Failed to check whitelist permissions';
          
          if (err.message.includes('address not whitelisted')) {
            errorMessage = 'You are not a whitelisted user';
          }else if (err.message.includes('Contract not deployed')) {
            errorMessage = 'Contract not deployed to the current network';
          } else if (err.code === 'NETWORK_ERROR') {
            errorMessage = 'Network connection error, please check your network settings';
          } else if (err.code === 'CALL_EXCEPTION') {
            errorMessage = 'Contract call failed, please check if the contract address is correct';
          }
          
        //  setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

 

      const handleReviewSubmit = async (templateId: number, approved: boolean, comments?: string) => {
       if (!account || !provider || !chainId) {
         toast.error('Please connect your wallet first');
         return;
       }
  
       if (!tokenId) {
         toast.error('Please select a user who has already minted KYC');
         return;
       }
  
       // Validate data
       if (!templateId) {
         toast.error('Please confirm the KYC ID');
         return;
       }
      const veristatus = approved ? 2 : 3;
         // Validate data
       if (!veristatus) {
         toast.error('Please confirm KYC approval');
         return;
       }
       // Set up the stepper
        setTitle(`Review KYC Level ${templateId} Data ID:${tokenId} as: ${veristatus}`);
        setSteps([
          'Validating form data', 
          'Preparing contract interaction',
          'Submitting transaction',
          'Waiting for confirmation'
        ]);
        setActiveStep(0);
        setOpen(true);
        setDialogError(null);
  
       try {
         // Step 1: Validate form data
         setMessage('Validating form data...');
         setDialogLoading(true);
         await new Promise(resolve => setTimeout(resolve, 500));
         nextStep(); 
    
          // Step 2: Prepare contract interaction
        setMessage('Preparing contract interaction...');
        const signer = provider.getSigner();
        const contract = createKYCContract.writable(chainId, signer);
        if (!contract) {
          throw new Error('Unable to get contract instance');
        }
        
        // Decide whether to perform gas estimation based on environment variables
        let gasOptions: any = {};
    
        if (process.env.REACT_APP_ENABLE_GAS_ESTIMATION === 'true') {
          try {
            // Prioritize using Polygon Gas Station API
            const gasFees = await getPolygonGasFees(chainId!);
            if (gasFees) {
              gasOptions.maxFeePerGas = ethers.BigNumber.from(gasFees.maxFeePerGas);
              gasOptions.maxPriorityFeePerGas = ethers.BigNumber.from(gasFees.maxPriorityFeePerGas);
            } else {
              // Fallback to traditional estimation
              const gasLimit = await contract.estimateGas.updateKYCStatus(templateId,tokenId,veristatus);
              gasOptions.gasLimit = gasLimit.mul(120).div(100); // Add a 20% gas buffer
              gasOptions.gasPrice = await provider.getGasPrice();
            }
          } catch (gasError) {
            console.warn('Gas estimation failed, using default values:', gasError);
            // Use default gas settings
            gasOptions.gasLimit = ethers.utils.parseUnits('300000', 'wei');
            gasOptions.gasPrice = await provider.getGasPrice();
          }
        }
        nextStep();
  
          // Step 3: Submit transaction
          setMessage('Submitting transaction...');
          const tx = await callContractWithRetry(() => contract.updateKYCStatus(templateId,tokenId,veristatus,gasOptions)) as any;
          
          nextStep();
  
          // Step 4: Wait for confirmation
          setMessage('Waiting for transaction confirmation...');
          const receipt = await tx.wait();
          
          if (receipt.status === 1) {
            setMessage(`Review for KYC Level ${templateId} Data ID:${tokenId} submitted successfully!`);
            setDialogLoading(false);  
            toast.success(`Review for KYC Level ${templateId} Data ID:${tokenId} submitted successfully!`);
          } else {
            throw new Error('Transaction failed');
          }
         
       } catch (err: any) {
          console.error(`Submission failed for review of KYC Level ${templateId} Data ID:${tokenId}!`, err);
          setDialogLoading(false);
          
          let errorMessage = 'Submission failed';
          if (err.code === 4001) {
            errorMessage = 'User cancelled the transaction';
          } else if (err.message.includes('insufficient funds')) {
            errorMessage = 'Insufficient balance';
          } else if (err.message) {
            errorMessage = err.message;
          }
          
          setDialogError(errorMessage);
          toast.error(errorMessage);
        }
     };
  // Handle Token ID input
  const handleTokenIdSubmit = () => {
    if (!tokenId.trim()) {
      console.error('Please enter a Token ID');
      return;
    }
    
    // Update URL
    navigate(`/assets-kyc/${tokenId}`);
  };

    // Handle Wallet input
  const handleWalletSubmit = async () => {
    if (!walletAddress.trim()) {
      console.error('Please enter a Wallet Address');
      return;
    }

     try {
      const contract = getContract();
      if (!contract) {
        toast.error('Please connect your wallet first');
        throw new Error('Please connect your wallet first');
      }   
      // Call the review method of the contract
      const txid = await contract.getTokenIdByWallet(walletAddress);
      
       // Update URL
    navigate(`/assets-kyc/${txid}`); 
      
    } catch (err: any) {
        console.error('Failed:', err);   
        toast.error('getTokenIdByWallet failed');
    }  
  };

  useEffect(() => {
    if (provider && account) {
     //  console.info('checkUserPermissions:', account);   
      checkUserPermissions();
    }
  }, [getContractWithSigner]);

  useEffect(() => {
    if (urlTokenId) {
      setTokenId(urlTokenId);
    }
  }, [urlTokenId]);

  // console.info('whitelistInfo permission:', permission);   
  // console.info('whitelistInfo isWhitelisted:', isWhitelisted);   
  //   console.info('whitelistInfo selectedKYCTypes:', selectedKYCTypes);   
  // console.info('whitelistInfo account:', account);   
  if (!account) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Alert severity="warning">
          Please connect your wallet first
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            onClick={() => {
              setError('');
              checkUserPermissions();
            }}
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!isWhitelisted) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. Only whitelisted users can view and review KYC data.
        </Alert>
      </Box>
    );
  }

  return (
    <>
     {isWhitelisted&&selectedKYCTypes&&permission&& (
     <>
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Page title and user information */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          KYC Review Center
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
   
      
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>  
                <Typography variant="h6" sx={{ wordBreak: 'break-all' }}>
                  Current User: {account ? `${account.slice(0, 6)}...${account.slice(-4)}` : 'Not Connected'}
                </Typography>
                             
                              
                              <Grid container spacing={2}> 
                                
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">In Whitelist:</Typography>
                                    <Chip 
                                      label={isWhitelisted ? 'Yes' : 'No'} 
                                      color={isWhitelisted ? 'success' : 'error'}
                                      size="small"
                                    />
                                  </Box>
                                </Grid>
                                
                                {/* <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">读权限:</Typography>
                                    <Chip 
                                      label={permission ==Permission.READ_ONLY ? '有' : '无'} 
                                      color={permission ==Permission.READ_ONLY ? 'success' : 'error'}
                                      size="small"
                                    />
                                  </Box>
                                </Grid>
                                
                                <Grid item xs={12} sm={6}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">写权限:</Typography>
                                    <Chip 
                                      label={permission ==Permission.READ_WRITE? '有' : '无'} 
                                      color={permission ==Permission.READ_WRITE? 'success' : 'error'}
                                      size="small"
                                    />
                                  </Box>
                                </Grid> */}
                                
                                <Grid item xs={12}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="body2">Permission Level:</Typography>
                                    <Chip 
                                      label={getPermissionText(permission)} 
                                      color={getPermissionColor(permission) as any}
                                      size="small"
                                    />
                                  </Box>
                                </Grid>
                                
                                {/* {permissionInfo.addedAt > 0 && (
                                  <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                      添加时间: {new Date(permissionInfo.addedAt * 1000).toLocaleString()}
                                    </Typography>
                                  </Grid>
                                )} */}
                              </Grid> 
                <Typography color="text.secondary" gutterBottom>
                  Accessible KYC Types
                </Typography>
                <Typography variant="h6"> 
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {selectedKYCTypes?.map((kycTypeId) => (
                        <Chip
                          key={kycTypeId}
                          label={`KYC-${kycTypeId}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box> 
                </Typography>
              </CardContent>
            </Card>
          </Grid>


          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>  
     <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Please enter the Wallet Address to view
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Wallet Address"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder="Please enter Wallet Address, format: 0x12345678901234567890123456780000090"
                fullWidth 
              />
              <Button
                variant="contained"
                onClick={handleWalletSubmit}

                disabled={!walletAddress.trim()}
              >
                View
              </Button>
            </Box>
          </Box>
             </CardContent>
            </Card>
          </Grid>
        </Grid>

   
          {/* <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Please enter the Token ID
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Token ID"
                value={tokenId}
                onChange={(e) => setTokenId(e.target.value)}
                placeholder="Please enter Token ID"
                fullWidth
                type="number"
              />
              <Button
                variant="contained"
                onClick={handleTokenIdSubmit}
                disabled={!tokenId.trim()}
              >
                View
              </Button>
            </Box>
          </Box> */}
      
     
      </Paper>

      {/* KYC Management Component */}
      {tokenId && (
        <AssetsKycManager 
          tokenId={tokenId}  
          selectedKYCTypes={selectedKYCTypes}
          permission={permission}
          isWhitelisted={isWhitelisted}
          onReviewSubmit={permission&&permission==Permission.READ_WRITE ? handleReviewSubmit : undefined} 
          //onReviewSubmit={permission&&permission==Permission.READ_WRITE ? handleReviewSubmit : undefined} 
          onResetSubmit={undefined}
        />
      )}
          {/* Stepper Dialog */}
            <SignMessageDialog onClose={endsubmitjob} />
    </Box>
    </>
    )}
    </>
  );
};

export default AssetsKycPage;